import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, Zap, TrendingUp, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProductCard from '@/components/ProductCard';
import CategoryCard from '@/components/CategoryCard';
import { BannerAd } from '@/components/AdBanner';
import { CATEGORIES } from '@/data/categories';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURES = [
  { id: 'physical', icon: Package, title: 'Physical Products', description: 'Quality items shipped directly to you' },
  { id: 'affiliate', icon: Zap, title: 'Affiliate Deals', description: 'Exclusive discounts from trusted partners' },
  { id: 'prices', icon: TrendingUp, title: 'Best Prices', description: 'Competitive pricing guaranteed' },
  { id: 'checkout', icon: ShieldCheck, title: 'Secure Checkout', description: 'Safe payments with Stripe' },
];

// Feature Card Component
const FeatureCard = ({ feature }) => (
  <div className="flex flex-col items-center text-center p-4">
    <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center mb-3">
      <feature.icon className="w-6 h-6" />
    </div>
    <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Archivo, sans-serif' }}>{feature.title}</h3>
    <p className="text-xs text-muted-foreground">{feature.description}</p>
  </div>
);

// Loading Skeleton Component
const ProductSkeleton = () => (
  <div className="rounded-lg border p-4 space-y-4">
    <div className="aspect-square skeleton-pulse"></div>
    <div className="h-4 skeleton-pulse w-3/4"></div>
    <div className="h-3 skeleton-pulse w-1/2"></div>
  </div>
);

// Product Grid Component
const ProductGrid = ({ products, loading, testId }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => <ProductSkeleton key={`skeleton-${i}`} />)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid={testId}>
      {products.map((product, index) => (
        <div key={product.id} style={{ animationDelay: `${index * 100}ms` }} className="animate-fade-in">
          <ProductCard product={product} />
        </div>
      ))}
    </div>
  );
};

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [affiliateProducts, setAffiliateProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      await axios.post(`${API}/seed`);
      const [featuredRes, affiliateRes] = await Promise.all([
        axios.get(`${API}/products?featured=true&limit=4`),
        axios.get(`${API}/products?product_type=affiliate&limit=4`)
      ]);
      setFeaturedProducts(featuredRes.data);
      setAffiliateProducts(affiliateRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const features = useMemo(() => FEATURES, []);

  return (
    <div className="animate-slide-up">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary rounded-full text-sm font-medium">
                <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse"></span>
                Now live with new products
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight" style={{ fontFamily: 'Archivo, sans-serif' }}>
                Your One-Stop
                <br />
                <span className="text-[#2563EB]">SALES</span> SPACE
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg">
                Discover premium physical products and exclusive affiliate deals. 
                Quality curated, prices optimized.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/products" data-testid="shop-now-btn">
                  <Button size="lg" className="gap-2 px-8">
                    Shop Now
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/products?product_type=affiliate" data-testid="browse-deals-btn">
                  <Button size="lg" variant="outline" className="gap-2 border-[#F97316] text-[#F97316] hover:bg-[#F97316]/10">
                    Browse Deals
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Bento Grid Hero */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-card rounded-lg p-6 border shadow-sm">
                  <div className="text-3xl font-bold text-[#2563EB]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>500+</div>
                  <div className="text-sm text-muted-foreground">Products Available</div>
                </div>
                <div className="aspect-square rounded-lg overflow-hidden border">
                  <img 
                    src="https://images.unsplash.com/photo-1759588071814-1ba7c5761af4?crop=entropy&cs=srgb&fm=jpg&w=400"
                    alt="Featured product"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[4/3] rounded-lg overflow-hidden border">
                  <img 
                    src="https://images.unsplash.com/photo-1606890541157-5b11f168e8cf?crop=entropy&cs=srgb&fm=jpg&w=400"
                    alt="Featured product"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="bg-[#F97316]/10 rounded-lg p-6 border border-[#F97316]/20">
                  <div className="text-3xl font-bold text-[#F97316]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>40%</div>
                  <div className="text-sm text-[#F97316]">Avg. Affiliate Savings</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-y bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature) => (
              <FeatureCard key={feature.id} feature={feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Shop by Category */}
      <section className="py-16 bg-gradient-to-b from-background to-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Archivo, sans-serif' }}>
              Shop by Category
            </h2>
            <p className="text-muted-foreground mt-2">Find exactly what you&apos;re looking for</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="category-grid">
            {CATEGORIES.map((category) => (
              <CategoryCard key={category.id} category={category} size="default" />
            ))}
          </div>
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BannerAd slot="homepage-top" />
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Archivo, sans-serif' }}>Featured Products</h2>
              <p className="text-muted-foreground mt-1">Hand-picked items just for you</p>
            </div>
            <Link to="/products" data-testid="view-all-products">
              <Button variant="ghost" className="gap-2">
                View All
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <ProductGrid products={featuredProducts} loading={loading} testId="featured-products-grid" />
        </div>
      </section>

      {/* Affiliate Deals Section */}
      {affiliateProducts.length > 0 && (
        <section className="py-16 bg-[#F97316]/5 border-y border-[#F97316]/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#F97316]/10 rounded-full text-sm font-medium text-[#F97316] mb-2">
                  <Zap className="w-4 h-4" />
                  Exclusive Deals
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Archivo, sans-serif' }}>Affiliate Partners</h2>
                <p className="text-muted-foreground mt-1">Save big with our curated affiliate deals</p>
              </div>
              <Link to="/products?product_type=affiliate" data-testid="view-all-deals">
                <Button variant="outline" className="gap-2 border-[#F97316] text-[#F97316] hover:bg-[#F97316]/10">
                  All Deals
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
            <ProductGrid products={affiliateProducts} loading={false} testId="affiliate-products-grid" />
          </div>
        </section>
      )}

      {/* Ad Banner Before CTA */}
      <section className="py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BannerAd slot="homepage-bottom" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-foreground text-background rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Archivo, sans-serif' }}>
              Ready to Start Shopping?
            </h2>
            <p className="text-background/70 mb-8 max-w-xl mx-auto">
              Browse our complete catalog of physical products and affiliate deals.
              Quality guaranteed, satisfaction promised.
            </p>
            <Link to="/products" data-testid="explore-catalog-btn">
              <Button size="lg" variant="secondary" className="gap-2 px-8">
                Explore Catalog
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
